/* ExposureViewComponent.js */

const EventEmitter = require("events").EventEmitter;
const PlatformSdk = require("@gluenetworks/platform-sdk-remote");
const Renderer = require("./ExposureViewRenderer");

const ExposureManagementPrefsEntity = PlatformSdk.entities.ws.ExposureManagementPrefs;

class ExposureViewComponent extends EventEmitter {
    constructor(
        logger,
        gameEditor,
        userSession,
        MUF,
        DeviceEntity,
        AdvisoryEntity,
        TriggerTypeEntity
    ) {
        super();
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._userSession = userSession;
        this._modalUserFeedback = MUF;
        this._DeviceEntity = DeviceEntity;
        this._AdvisoryEntity = AdvisoryEntity;
        this._TriggerTypeEntity = TriggerTypeEntity;
        this._activeOrgId = userSession.activeOrganization.id;
        this._device = undefined;
        this._qualification = undefined;
        // advisoryId -> {advisoryId, sir, firstFixed, advisoryTitle}. Populated
        // from the Advisory entity (FE-readable) to enrich the qualification
        // table. Empty when enrichment is unavailable.
        this._advisoriesById = new Map();
        // advisoryId -> ExposureRule pristine props (predicate + extraction
        // provenance), feeding the CSV export's rule/provenance columns. Empty
        // when rule enrichment is unavailable.
        this._rulesByAdvisoryId = new Map();

        this._renderer = new Renderer(this);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {
        if (!this._device || typeof this._device.on !== "function") {
            return;
        }
        this._unregisterListeners();
        this._onExposureActivityChanged = this._onExposureActivityChanged.bind(this);
        this._onExposureVerdictChanged = this._onExposureVerdictChanged.bind(this);
        this._device.on("ExposureActivityPropertyUpdated", this._onExposureActivityChanged);
        this._device.on("ExposureStatusPropertyUpdated", this._onExposureActivityChanged);
        this._device.on("ExposureEvaluatedOnPropertyUpdated", this._onExposureVerdictChanged);
    }

    _unregisterListeners() {
        if (!this._device || typeof this._device.removeListener !== "function") {
            return;
        }
        if (this._onExposureActivityChanged) {
            this._device.removeListener(
                "ExposureActivityPropertyUpdated",
                this._onExposureActivityChanged
            );
            this._device.removeListener(
                "ExposureStatusPropertyUpdated",
                this._onExposureActivityChanged
            );
        }
        if (this._onExposureVerdictChanged) {
            this._device.removeListener(
                "ExposureEvaluatedOnPropertyUpdated",
                this._onExposureVerdictChanged
            );
        }
    }

    _onExposureActivityChanged() {
        this.emit("ExposureLifecycleChanged");
    }

    _onExposureVerdictChanged() {
        this.emit("ExposureVerdictsChanged");
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        this._unregisterListeners();

        return this._renderer.shutdown().then(
            () => {
                this._modalUserFeedback = undefined;
                this._userSession = undefined;
                this._DeviceEntity = undefined;
                this._AdvisoryEntity = undefined;
                this._TriggerTypeEntity = undefined;
                this._device = undefined;
                this._qualification = undefined;
                this._advisoriesById = undefined;
                this._rulesByAdvisoryId = undefined;
                this._logger = undefined;
                this._gameEditor = undefined;
                this._renderer = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "ExposureViewComponent.shutdown() error.");
            }
        );
    }

    get gameEditor() {
        return this._gameEditor;
    }

    get logger() {
        return this._logger;
    }

    /**
     * Loads the DeviceCveQualification entity for the device. The
     * DeviceCveQualification shares the Device's id (1:1) so the device id is
     * used directly as the entity id. Resolves with undefined when no
     * qualification has been run for the device yet.
     *
     * Uses the static load (single retrieveEntity read) rather than retrieve:
     * retrieve starts a change feed, and this view re-loads on every activation
     * anyway, so a live feed buys nothing for what it costs.
     *
     * The renderer still reads this row through getPristineProperties: the SDK now
     * generates a DeviceCveQualification class, but depFreshness is newer than the
     * published SDK, so no accessor exists for it yet.
     */
    whenModelIsReady() {
        return this._gameEditor
            .whenDeviceIsReady()
            .then(() => {
                this._device = this._gameEditor.getDevice();
                return this._DeviceEntity.load(
                    this._activeOrgId,
                    "DeviceCveQualification",
                    this._device.getId()
                );
            })
            .then((qualification) => {
                this._qualification = qualification;
                this._registerListeners();
                // Advisory + exposure-rule enrichment is best-effort: never let
                // it reject the whole view (the qualification is the essential
                // data). Rules feed the CSV export's predicate/provenance columns.
                return Promise.all([
                    this._loadAdvisories(),
                    this._loadExposureRules(),
                    this._loadFreshnessPrefs(),
                ]);
            })
            .catch((err) => {
                this._logger.warn(err, "ExposureViewComponent.whenModelIsReady() error");
                return Promise.reject(err);
            });
    }

    /**
     * Loads the Advisory entities referenced by the qualification (winning
     * advisoryId plus contributing advisories) so the renderer can show advisory
     * title/severity/firstFixed. Never rejects: on failure the map is left empty
     * and the view degrades to what the qualification rows already carry.
     */
    _loadAdvisories() {
        this._advisoriesById = new Map();
        const qualification = this._qualification;
        if (!qualification || typeof qualification.getPristineProperties !== "function") {
            return Promise.resolve();
        }
        const props = qualification.getPristineProperties();
        const rows = (props && props.qualifications) || [];
        const ids = new Set();
        rows.forEach((row) => {
            if (row.advisoryId) {
                ids.add(row.advisoryId);
            }
            (row.contributingAdvisoryIds || []).forEach((id) => {
                if (id) {
                    ids.add(id);
                }
            });
        });
        if (ids.size === 0) {
            return Promise.resolve();
        }
        return this._AdvisoryEntity
            .loadByQuery(
                this._activeOrgId,
                "Advisory",
                {id: {$in: Array.from(ids)}},
                {id: true, displayName: true, data: true}
            )
            .then((advisories) => {
                (advisories || []).forEach((advisory) => {
                    const advProps =
                        typeof advisory.getPristineProperties === "function"
                            ? advisory.getPristineProperties()
                            : advisory;
                    const data = (advProps && advProps.data) || {};
                    // The entity id is NOT a pristine property (EntityCreator
                    // routes it to _setId) — read it via the getter or the map
                    // silently stays empty and every advisory column blanks out.
                    const advisoryId =
                        typeof advisory.getId === "function"
                            ? advisory.getId()
                            : advProps && advProps.id;
                    if (advisoryId) {
                        this._advisoriesById.set(advisoryId, {
                            advisoryId: advisoryId,
                            displayName: (advProps && advProps.displayName) || null,
                            sir: data.sir || null,
                            firstFixed: data.firstFixed || null,
                            advisoryTitle: data.advisoryTitle || null,
                            cvssBaseScore: data.cvssBaseScore || null,
                            summary: data.summary || null,
                        });
                    }
                });
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "ExposureViewComponent._loadAdvisories() error — advisory enrichment unavailable"
                );
                this._advisoriesById = new Map();
            });
    }

    /**
     * Loads the ExposureRule entities (the LLM-extracted predicates) for every
     * advisory the qualification references, keyed by advisoryId — the CSV
     * export joins them per row for the rule/provenance columns. Same
     * unregistered-type loadByQuery pattern as _loadAdvisories, and the same
     * silent-degrade contract: on failure the map stays empty and the export
     * columns degrade rather than blocking the download. The projection is
     * deliberately WIDE — a narrow one silently empties the export columns.
     */
    _loadExposureRules() {
        this._rulesByAdvisoryId = new Map();
        const qualification = this._qualification;
        if (!qualification || typeof qualification.getPristineProperties !== "function") {
            return Promise.resolve();
        }
        const props = qualification.getPristineProperties();
        const rows = (props && props.qualifications) || [];
        const ids = new Set();
        rows.forEach((row) => {
            if (row.advisoryId) {
                ids.add(row.advisoryId);
            }
            (row.contributingAdvisoryIds || []).forEach((id) => {
                if (id) {
                    ids.add(id);
                }
            });
        });
        if (ids.size === 0) {
            return Promise.resolve();
        }
        return this._DeviceEntity
            .loadByQuery(
                this._activeOrgId,
                "ExposureRule",
                {advisoryId: {$in: Array.from(ids)}},
                {
                    id: true,
                    advisoryId: true,
                    advisoryName: true,
                    status: true,
                    reviewStatus: true,
                    version: true,
                    predicate: true,
                    provenance: true,
                    extraction: true,
                    extractionError: true,
                    extractedAt: true,
                    extractedByModel: true,
                    llmConnectionId: true,
                    advisorySource: true,
                }
            )
            .then((rules) => {
                (rules || []).forEach((rule) => {
                    const ruleProps =
                        typeof rule.getPristineProperties === "function"
                            ? rule.getPristineProperties()
                            : rule;
                    if (ruleProps && ruleProps.advisoryId) {
                        this._rulesByAdvisoryId.set(ruleProps.advisoryId, ruleProps);
                    }
                });
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "ExposureViewComponent._loadExposureRules() error — rule enrichment unavailable"
                );
                this._rulesByAdvisoryId = new Map();
            });
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    /**
     * Best-effort load of the org freshness windows (ExposureManagementPrefs is
     * a tenant singleton). Never rejects: null prefs render the freshness chips
     * as "unknown", which is the fail-closed presentation.
     * @returns {Promise}
     */
    _loadFreshnessPrefs() {
        this._freshnessPrefs = null;
        // ExposureManagementPrefs is a tenant singleton, so the entity's own loader takes
        // the org id and resolves the row by it. All three windows have generated getters,
        // so the chips read them directly rather than through the pristine bag.
        return ExposureManagementPrefsEntity.loadExposureManagementPrefs(this._activeOrgId, {
            id: true,
            configStaleDays: true,
            advisoryStaleDays: true,
            verdictStaleDays: true,
        })
            .then((prefs) => {
                if (prefs) {
                    this._freshnessPrefs = {
                        configStaleDays: prefs.configStaleDays,
                        advisoryStaleDays: prefs.advisoryStaleDays,
                        verdictStaleDays: prefs.verdictStaleDays,
                    };
                }
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "ExposureViewComponent - could not load freshness prefs (chips read unknown)"
                );
            });
    }

    getQualification() {
        return this._qualification;
    }

    /** Org freshness windows for the dependency chips; null when unreadable. */
    getFreshnessPrefs() {
        return this._freshnessPrefs || null;
    }

    getAdvisoriesById() {
        return this._advisoriesById;
    }

    getRulesByAdvisoryId() {
        return this._rulesByAdvisoryId || new Map();
    }

    /**
     * The device's exposure lifecycle stamps, written by the backend alongside each verdict:
     * `exposureStatus` (RUNNING | CURRENT | STALE | ERROR) and `exposureEvaluatedOn`.
     *
     * Read defensively off pristine properties rather than through typed getters. The stamps are
     * written by the qualification work with skipHooks and are not declared in any schema in this
     * workspace, so a generated accessor may not exist; a missing field must degrade to "no status
     * banner", never to a broken view.
     *
     * @returns {{status: String|null, evaluatedOn: *}} Nulls when the device has never been
     *   qualified or the fields are not present.
     */
    getExposureLifecycle() {
        let props = {};
        try {
            props = (this._device && this._device.getPristineProperties()) || {};
        } catch (err) {
            this._logger.warn(err, "ExposureViewComponent.getExposureLifecycle() error");
        }
        return {
            status: props.exposureStatus || null,
            evaluatedOn: props.exposureEvaluatedOn || null,
            activity: props.exposureActivity || null,
            activityOn: props.exposureActivityOn || null,
        };
    }

    getDeviceEntity() {
        return this._DeviceEntity;
    }

    /**
     * The device this view is showing, or null before whenModelIsReady() resolves. Keys the
     * navigation memory, so it is read defensively: a device that cannot answer for its id must
     * cost the reader a remembered scroll position, never a broken view.
     */
    getDeviceId() {
        try {
            return (this._device && this._device.getId()) || null;
        } catch (err) {
            this._logger.warn(err, "ExposureViewComponent.getDeviceId() error");
            return null;
        }
    }

    getTriggerTypeEntity() {
        return this._TriggerTypeEntity;
    }

    getActiveOrgId() {
        return this._activeOrgId;
    }

    getUserFeedback() {
        return this._modalUserFeedback;
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = ExposureViewComponent;

ExposureViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.userSession",
    "$teve.ui.userFeedback",
    "$teve.entities.Device",
    "$teve.entities.Advisory",
    "$teve.entities.TriggerType",
];

ExposureViewComponent.$displayDetails = {
    type: "View",
    name: "Exposure",
    icon: "devicemanager.qualify_cves",
    shown: true,
    title: "Exposure",
    order: 8,
};
